import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import AdAssetMediaPreview from './AdAssetMediaPreview'
import FormDrawerWrapper from '../../../../features/components/FormDrawerWrapper'
import EditSectionsList from '../../../../features/components/Forms/EditForm/EditSectionsList'
import NameSectionPreview from '../../../ReusableFormSections/NameSection/NameSectionPreview'
import NameSection from './Sections/NameSection'
import LinkSectionForm from './Sections/LinkSection/LinkSectionForm'
import LinkSectionPreview from './Sections/LinkSection/LinkSectionPreview'
import AssetMediaSectionForm from './Sections/AssetMediaSection/AssetMediaSectionForm'
import AssetMediaSectionPreview from './Sections/AssetMediaSection/AssetMediaSectionPreview'
import ThirdPartySectionPreview from './Sections/ThirdPartyTagSection/ThirdPartySectionPreview'
import ThirdPartySectionForm from './Sections/ThirdPartyTagSection/ThirdPartySectionForm'
import DimensionsSectionPreview from './Sections/DimensionsSection/DimensionsSectionPreview'
import DimensionsSectionForm from './Sections/DimensionsSection/DimensionsSectionForm'

import useManageEditFormData from '../../../../hooks/formHooks/useManageEditFormData'

import { createJsonFromQueryString } from '../../../../helpers/url'
import { insertIf } from '../../../../helpers/common'

import { getAd, clearAd, clearUpdateAd } from '../../../../modules/actions/ads'
import {
  adSelector,
  adLoadingSelector,
  adWasLoadedSelector,
  adWasUpdatedSelector,
  adUpdateErrorSelector,
  adUpdateIsLoadingSelector
} from '../../../../modules/selectors/ads'
import { selectedDV360AdAccountSelector } from '../../../../modules/selectors/adAccounts'
import { lineItemSelector } from '../../../../modules/selectors/lineItems'

import { AD_DV360_EDIT } from '../../../../constants/forms'
import { NAME } from '../../../ReusableFormSections/NameSection/fields'
import { DV_360_PLATFORM } from '../../../../constants/selectLists/platformList'
import { CLICK_THROUGH_LINK, MEDIA_HEIGHT, MEDIA_WIDTH, THIRD_PARTY_TAG, VAST_TAG_URL } from '../fields'
import {
  LINE_ITEM_TYPE,
  VIDEO_DEFAULT
} from '../../UploadDV360AdCreative/UploadDV360AdCreativeForm/LineItemForm/fields'
import { MEDIA_ITEMS } from '../AdDv360Create/AdDv360CreateForm/Routes/CreativeFileUploadRoute/fields'

// here defined initial open states for function AdDv360Edit sections
const sectionsInitialOpenStates = {
  [NAME]: false,
  [CLICK_THROUGH_LINK]: false,
  [MEDIA_ITEMS]: false
}

function AdDv360Edit() {
  const dispatch = useDispatch()

  const ad = useSelector(adSelector)
  const adIsLoading = useSelector(adLoadingSelector)
  const adWasLoaded = useSelector(adWasLoadedSelector)
  const adWasUpdated = useSelector(adWasUpdatedSelector)
  const { [LINE_ITEM_TYPE]: lineItemType } = useSelector(lineItemSelector)
  const { id: selectedAdAccountId } = useSelector(selectedDV360AdAccountSelector)

  // if ad is internal, it appears in query params when we open edit form
  const { internal: internalQueryParam } = createJsonFromQueryString(window.location.search)

  const isInternalAd = internalQueryParam === 'true'

  // to avoid loading representation
  const isInitialDataLoading = adIsLoading || !adWasLoaded

  const getItemDataHandler = useCallback(
    selectedEditItemId => {
      dispatch(
        getAd(
          {
            id: selectedEditItemId,
            account: selectedAdAccountId,
            ...(isInternalAd && {
              internal: true
            })
          },
          DV_360_PLATFORM
        )
      )
    },
    [dispatch, selectedAdAccountId, isInternalAd]
  )

  const clearItemDataHandler = useCallback(() => {
    dispatch(clearAd())
  }, [dispatch])

  useManageEditFormData({
    formName: AD_DV360_EDIT,
    loadedDataId: ad.id,
    getDataHandler: getItemDataHandler,
    clearDataHandler: clearItemDataHandler
  })

  // here defined all edit sections for AdDv360Edit
  const editSections = useMemo(
    () => [
      {
        sectionName: NAME,
        title: 'Name',
        PreviewComponent: <NameSectionPreview dataSelector={adSelector} />,
        FormComponent: <NameSection />
      },
      ...insertIf(ad.exit_events?.[0]?.url, {
        sectionName: CLICK_THROUGH_LINK,
        title: 'Link',
        PreviewComponent: <LinkSectionPreview ad={ad} />,
        FormComponent: <LinkSectionForm />
      }),
      ...insertIf(ad.assets, {
        sectionName: MEDIA_ITEMS,
        title: lineItemType === VIDEO_DEFAULT ? 'Video' : 'Image',
        PreviewComponent: <AssetMediaSectionPreview ad={ad} />,
        FormComponent: <AssetMediaSectionForm />
      }),
      ...insertIf(ad[THIRD_PARTY_TAG] || ad[VAST_TAG_URL], {
        sectionName: 'thirdPartyTag',
        title: ad[VAST_TAG_URL] ? 'VAST Tag' : 'JavaScript Tag',
        PreviewComponent: <ThirdPartySectionPreview ad={ad} />,
        FormComponent: <ThirdPartySectionForm />
      }),
      ...insertIf(ad.dimensions?.[MEDIA_WIDTH] && ad.dimensions?.[MEDIA_HEIGHT], {
        sectionName: 'dimensions',
        title: 'Dimensions',
        PreviewComponent: <DimensionsSectionPreview ad={ad} />,
        FormComponent: <DimensionsSectionForm />
      })
    ],
    [ad, lineItemType]
  )

  const handleClearUpdateAd = useCallback(() => {
    dispatch(clearUpdateAd())
  }, [dispatch])

  const formProps = useMemo(
    () => ({
      successSubmit: adWasUpdated,
      errorSelector: adUpdateErrorSelector,
      isLoadingSelector: adUpdateIsLoadingSelector,
      clearEditItem: handleClearUpdateAd,
      isInternalAd
    }),
    [adWasUpdated, handleClearUpdateAd, isInternalAd]
  )

  return (
    <FormDrawerWrapper title="Update the settings of your display ad" formName={AD_DV360_EDIT} showOpenButton={false}>
      {adWasLoaded && ad.assets && <AdAssetMediaPreview ad={ad} />}
      <EditSectionsList
        editSections={editSections}
        isEditDataLoading={isInitialDataLoading}
        editItemData={ad}
        sectionsInitialOpenStates={sectionsInitialOpenStates}
        formProps={formProps}
      />
    </FormDrawerWrapper>
  )
}

export default AdDv360Edit
